// alterTrigger — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'alter-trigger' })` op (DSE2 §3.D6).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D6 — Trigger shell UI.
//
// Per-engine surface (per DSE2 dialect.trigger):
//   - mssql:  {ENABLE|DISABLE} TRIGGER <name> ON <target>
//             — target REQUIRED.
//   - pgsql:  ALTER TABLE <target> {ENABLE|DISABLE} TRIGGER <name>
//             — target REQUIRED.
//   - oracle: ALTER TRIGGER <name> {ENABLE|DISABLE}
//             — target rejected (oracle binds the trigger to its own scope).
//   - mysql:  unsupported — no ALTER TRIGGER. Drop + recreate.
//   - sqlite: unsupported — no ALTER TRIGGER. Drop + recreate.
//
// Cross-engine surface is limited to ENABLE / DISABLE. Renames live on the
// drop+recreate path (mysql/sqlite) or via the engine's CREATE OR REPLACE /
// OR ALTER on the supporting engines.

import type { SqlEngine, QualifiedName } from '../../../../sql-grammar/model';
import type { DdlEdit, TriggerChange } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface AlterTriggerArgs {
  readonly triggerName: QualifiedName;
  readonly target?: QualifiedName;
  readonly change: TriggerChange;
}

export function buildAlterTriggerEdit(args: AlterTriggerArgs): DdlEdit {
  return {
    op: 'alter-trigger',
    triggerName: args.triggerName,
    ...(args.target ? { target: args.target } : {}),
    change: args.change,
  };
}

export async function commitAlterTrigger(
  ctx: VqeDdlEditContext,
  args: AlterTriggerArgs,
): Promise<void> {
  const edit = buildAlterTriggerEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const ALTER_TRIGGER_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'postgres', 'oracle',
] as const);

export function isAlterTriggerSupported(engine: SqlEngine): boolean {
  return ALTER_TRIGGER_SUPPORTED.has(engine);
}

export function getAlterTriggerUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mysql' || engine === 'sqlite') {
    return (
      `${engine === 'mysql' ? 'MySQL' : 'SQLite'} has no ALTER TRIGGER. `
      + 'Drop the trigger and recreate it to change anything beyond the body.'
    );
  }
  return '';
}

export function validateAlterTriggerForEngine(
  engine: SqlEngine,
  args: AlterTriggerArgs,
): string | null {
  if (!isAlterTriggerSupported(engine)) {
    return getAlterTriggerUnsupportedTooltip(engine);
  }
  if (args.triggerName.name.trim().length === 0) {
    return 'Trigger name is required.';
  }
  if (engine === 'mssql' || engine === 'postgres') {
    if (!args.target || args.target.name.trim().length === 0) {
      return `${engine === 'mssql' ? 'mssql' : 'PostgreSQL'} ALTER TRIGGER requires the target table — pass the trigger's owning table.`;
    }
  } else if (engine === 'oracle' && args.target) {
    return 'Oracle ALTER TRIGGER does not name the target table.';
  }
  return null;
}
